import { useEffect, useState } from 'react'
import { ISelectedProvider } from '../pages/your-connections/your-connections'
import {
  useAccount,
  useSnackbarContext,
  useDialogContext,
  storage,
} from '@yuno/dashboard-container-mfe'
import {
  useGetIntegrationParams,
  useArchiveConnection,
  useGetIntegrationByAccount,
  usePatchConnection,
  useGetProviderParams,
  useDeleteConnection,
} from '@yuno/dashboard-api-mfe'
import type { UseQueryResult } from 'react-query'
import { Organization } from '@yuno/types'
import { ConstantText, providerLocationCoverage } from '../utils/constants'
import { useTranslation } from 'react-i18next'

interface params {
  connectionCode: string
  archive: boolean
  provider: string
  should_validate?: boolean
}

const useYourconnectionsHook = () => {
  const [openDrawerForm, setOpenDrawerForm] = useState<boolean>(false)
  const [selectedProvider, setSelectedProvider] = useState<ISelectedProvider>()
  const [providerParams, setProvider] = useState<params>()
  const [drawerData, setDrawerData] = useState<ISelectedProvider>()
  const [connectionCode, setConnection] = useState<string>()
  const [enabled, setEnabled] = useState<boolean>(false)
  const account = useAccount()
  const patchConnection = usePatchConnection()
  const { t } = useTranslation()

  const {
    data,
    isLoading,
    isFetching,
    remove,
    refetch,
  }: UseQueryResult<Organization.Integration[], unknown> =
    useGetIntegrationByAccount({ accountCode: account?.code })

  const providerResponse: UseQueryResult<
    Organization.IntegrationParams[],
    unknown
  > = useGetProviderParams({ id: selectedProvider?.provider_id })

  const connectionResponse: UseQueryResult<
    Organization.IntegrationParams[],
    unknown
  > = useGetIntegrationParams({
    integration_code: connectionCode + '?encryptTestValue=true',
    enabled,
  })
  const archiveMutation = useArchiveConnection()
  const deleteMutation = useDeleteConnection()
  const { onOpenSnackbar } = useSnackbarContext()
  const { onOpenDialog, closeIcon } = useDialogContext()

  useEffect(() => {
    return () => remove()
  }, [])

  useEffect(() => {
    if (connectionResponse.status === 'success') {
      const fields = providerResponse.data
      setDrawerData({
        ...selectedProvider,
        countries: providerLocationCoverage?.[selectedProvider?.provider_id],
        fields: fields?.params,
        paymentMethods: fields?.paymentMethods,
      })
      handleOpenDrawer()
    }
  }, [connectionResponse.status])

  useEffect(() => {
    if (providerResponse.status === 'success') {
      setConnection(selectedProvider?.code)
      setEnabled(true)
    }
  }, [providerResponse.status])

  const handlerFetchProviderParams = (code?: string) => {
    setSelectedProvider(data.find((el) => el.code === code))
  }

  const handleOpenDrawer = () => {
    setOpenDrawerForm(true)
  }

  const resetForm = () => {
    connectionResponse.remove()
    providerResponse.remove()
    patchConnection.reset()
    setEnabled(false)
  }

  const handleCloseDrawer = () => {
    setOpenDrawerForm(false)
    setSelectedProvider(null)
    resetForm()
  }

  const handleSubmit = (data) => {
    let connectionObj = {
      provider_id: selectedProvider.provider_id,
      ...data,
    }
    patchConnection.mutate({ payload: connectionObj, connectionCode })
  }

  const acceptButton = (connectionCode, archive) => {
    closeIcon()
    archiveMutation.mutate({ payload: { archive }, connectionCode })
  }

  const handlerArchive = (
    connectionCode,
    archive,
    provider,
    should_validate,
  ) => {
    setProvider({ connectionCode, archive, provider, should_validate })
  }

  const deleteConnection = (connectionCode) => {
    closeIcon()
    deleteMutation.mutate(connectionCode)
  }

  const showDeleteDialog = (item) => {
    const buttons = [
      {
        variant: 'tertiary',
        label: t('cancel'),
        size: 'small',
        event: closeIcon,
      },
      {
        variant: 'primary',
        label: t('yesDelete'),
        size: 'small',
        event: deleteConnection.bind(null, item.code),
      },
    ]
    const texts = {
      text1: t('deleteConfirm'),
      text2: `${t('deleteConfirmFinal')} ${item.provider_name}?`,
    }
    const title = `${t('delete')} ${item.provider_name}`
    onOpenDialog(title, texts, buttons)
  }

  const showArchiveDialog = () => {
    const { connectionCode, archive, provider } = providerParams
    const title = `${archive ? 'Archive' : 'Activate'} ${provider}`
    const buttons = [
      {
        variant: 'tertiary',
        label: t('cancel'),
        size: 'small',
        event: closeIcon,
      },
      {
        variant: 'primary',
        label: `Yes, ${archive ? 'archive' : 'activate'}`,
        size: 'small',
        event: () => acceptButton(connectionCode, archive),
      },
    ]

    const texts = archive
      ? {
          text1: ConstantText.ARCHIVE_CONNECTION_1,
          text2: ConstantText.ARCHIVE_CONNECTION_2,
        }
      : {
          text1: ConstantText.ACTIVATE_CONNECTION_1,
          text2: ConstantText.ACTIVATE_CONNECTION_2,
        }

    onOpenDialog(title, texts, buttons)
  }

  useEffect(() => {
    if (providerParams) {
      const { connectionCode, archive, should_validate } = providerParams
      !should_validate
        ? showArchiveDialog()
        : archiveMutation.mutate({
            payload: { archive, should_validate },
            connectionCode,
          })
    }
  }, [providerParams])

  useEffect(() => {
    if (
      archiveMutation.isError &&
      archiveMutation.error.response.data.statusCode !== 400
    ) {
      onOpenSnackbar(
        ConstantText.ERROR_ARCHIVE,
        'error',
        'Info',
        { vertical: 'bottom', horizontal: 'center' },
        '375px',
      )
    }
    if (
      archiveMutation.isError &&
      archiveMutation.error.response.data.statusCode === 400
    ) {
      const title = `Archive ${providerParams.provider}`
      const buttons = [
        {
          variant: 'primary',
          label: t('gotIt'),
          size: 'small',
          event: closeIcon,
        },
      ]

      const texts = {
        text1: ConstantText.ACTIVATE_CHECKOUT_1,
        text2: ConstantText.ACTIVATE_CHECKOUT_2,
      }
      onOpenDialog(title, texts, buttons)
    }
  }, [archiveMutation.isError])

  useEffect(() => {
    if (archiveMutation.isSuccess) {
      if (archiveMutation.variables.payload.should_validate) {
        showArchiveDialog()
      } else {
        refetch()
      }
    }
  }, [archiveMutation.isSuccess])

  useEffect(() => {
    let archive =
      archiveMutation.isSuccess && archiveMutation.variables.payload.archive
    if (archiveMutation.isSuccess) {
      onOpenSnackbar(
        archive === 'archive'
          ? t('connectionSuccessfullyArchived')
          : t('connectionActiveSuccessfully'),
        'success',
        'CheckCircle',
      )
      archiveMutation.reset()
    }
  }, [data])

  useEffect(() => {
    if (patchConnection.isSuccess) {
      handleCloseDrawer()
      refetch()
      onOpenSnackbar(t('connectionSuccessfullyEdit'), 'success', 'CheckCircle')
      storage.removeItem('tempValuesForm')
    }
  }, [patchConnection.isSuccess])

  useEffect(() => {
    const errorDuplicateName =
      patchConnection.error?.response.data.message !==
      ConstantText.ERROR_MESSAGE_NAME
    if (patchConnection.isError && errorDuplicateName) {
      handleCloseDrawer()
      refetch()
      onOpenSnackbar(
        t('cantEditConnection'),
        'error',
        'Info',
        { vertical: 'bottom', horizontal: 'center' },
        '375px',
      )
    }
  }, [patchConnection.isError])

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      handleCloseDrawer()
      refetch()
      onOpenSnackbar(
        t('deleteConnectionSuccessfully'),
        'success',
        'CheckCircle',
      )
    }
  }, [deleteMutation.isSuccess])

  useEffect(() => {
    if (deleteMutation.isError) {
      handleCloseDrawer()
      refetch()
      onOpenSnackbar(
        t('cantDeleteConnection'),
        'error',
        'Info',
        { vertical: 'bottom', horizontal: 'center' },
        '375px',
      )
    }
  }, [deleteMutation.isError])

  // const newConnectionResponse = () => {
  //   if(connectionResponse.data){
  //     const connectionR = { ...connectionResponse }
  //     connectionR.data.integration_type = 'Transacción Completa'
  //     return connectionR
  //   }
  // }

  return {
    patchConnection,
    connectionResponse,
    handleSubmit,
    drawerData,
    selectedProvider,
    providerResponse,
    resetForm,
    openDrawerForm,
    handleCloseDrawer,
    isLoading: isLoading || isFetching,
    data,
    handlerFetchProviderParams,
    handlerArchive,
    showDeleteDialog,
  }
}

export default useYourconnectionsHook
